<template>
  <page>
    <page-header-new
      :title="$t('Channels_SmsSettings')"
      :breadcrumbs="breadcrumbs"
    />
    <page-content>
      <rest-page :fetch-endpoint="fetchSmsSubscriptionEndpoint">
        <rest-form
          ref="form"
          :save-endpoint="saveProjectEndpoint"
          :model="subscriptionModel"
          :disabled="isDisabled"
        >
          <form-section>
            <el-form :model="subscriptionModel" :disabled="isDisabled">
              <el-row>
                <el-col :span="9">
                  <form-section-header
                    :title="$t('SmsSettings_Settings_Title')"
                    :description="$t('SmsSettings_Settings_Description')"
                  >
                    <template slot="controls">
                      <save-button :click="() => $refs['form'].save()" />
                    </template>
                  </form-section-header>
                  <form-item
                    class="margin-right"
                    prop="sms_subscription_logo_file"
                    :label="$t('SmsSettings_Field_Logo')"
                  >
                    <icon-upload
                      :value="subscriptionModel.sms_subscription_page.logo"
                      :file.sync="subscriptionModel.sms_subscription_logo_file"
                      accepts=".gif,.jpg,.jpeg,.png,.ico"
                      size="medium"
                    />
                  </form-item>

                  <div class="page-styles">
                    <color-picker-field
                      v-model="
                        subscriptionModel.sms_subscription_page.background
                      "
                      :label-text="$t('SmsSettings_Field_Background')"
                    />

                    <color-picker-field
                      v-model="
                        subscriptionModel.sms_subscription_page.highlight
                      "
                      :label-text="$t('SmsSettings_Field_Highlight')"
                    />
                  </div>

                  <form-item
                    prop="mobile_number_change_enabled"
                    :label="$t('SmsSettings_Field_AllowChangeNumber_Title')"
                    :description="
                      $t('SmsSettings_Field_AllowChangeNumber_Desc')
                    "
                  >
                    <toggle
                      v-model="
                        subscriptionModel.sms_subscription_page
                          .mobile_number_change_enabled
                      "
                    />
                  </form-item>

                  <form-item
                    prop="manage_preferences_enabled"
                    :label="$t('SmsSettings_AllowManagePreferences_Title')"
                    :description="$t('SmsSettings_AllowManagePreferences_Desc')"
                  >
                    <toggle
                      v-if="isSubscriptionPreferencesEnabled"
                      v-model="
                        subscriptionModel.sms_subscription_page
                          .manage_preferences_enabled
                      "
                    />
                    <toggle
                      v-else
                      :disabled="true"
                      :text="
                        $t('SmsSettings_AllowManagePreferences_NotEnabled')
                      "
                    />
                  </form-item>
                </el-col>

                <el-col :span="15">
                  <unsubscribe-preview
                    type="sms"
                    :logo-url="logo"
                    :can-manage-subscription="1"
                    :allow-change-field="
                      subscriptionModel.sms_subscription_page
                        .mobile_number_change_enabled
                    "
                    :background-color="
                      subscriptionModel.sms_subscription_page.background
                    "
                  />
                </el-col>
              </el-row>
            </el-form>
          </form-section>
        </rest-form>
      </rest-page>

      <form-section
        :title="$t('SmsFromName_List_Title')"
        :description="$t('SmsFromName_List_Description')"
      >
        <template slot="controls">
          <el-button
            type="primary"
            icon="el-icon-circle-plus-outline"
            @click="createSmsFromName"
          >
            {{ $t('SmsFromName_Form_CreateButton') }}
          </el-button>
        </template>
        <rest-list-editable
          :payload="fetchSmsFromNameEndpoint"
          :rules="smsFromNameRules"
          :add-endpoint="addSmsFromNameEndpoint"
          :update-endpoint="updateSmsFromNameEndpoint"
          :show-header="false"
          :outline="false"
          ref="from-name-list"
        >
          <rest-list-column
            :editable="true"
            prop="from_name"
            :title="$t('SmsFromName_Field_FromName')"
          />
          <rest-list-column-actions :editable="true">
            <template slot-scope="scope">
              <rest-list-action-delete
                :endpoint="rowDeleteSmsFromNameEndpoint(scope.row)"
                :name="scope.row.from_name"
              />
            </template>
          </rest-list-column-actions>
        </rest-list-editable>
      </form-section>

      <rest-page :fetch-endpoint="fetchExtendedSmsOptOutEndpoint">
        <rest-form
          ref="opt-out-form"
          :save-endpoint="saveProjectEndpoint"
          :model="optOutModel"
          :disabled="isDisabled"
        >
          <form-section>
            <el-form :model="optOutModel" :disabled="isDisabled">
              <form-section-header
                :title="$t('SmsSettings_OptOutSettings_Title')"
              >
                <template slot="controls">
                  <save-button :click="() => $refs['opt-out-form'].save()" />
                </template>
              </form-section-header>
              <el-row>
                <el-col :span="12">
                  <form-item class="sms-opt-out">
                    <toggle
                      v-model="optOutModel.sms_opt_out"
                      :text="$t('SmsSettings_OptOut_Title')"
                    />
                  </form-item>
                  <form-item v-if="optOutModel.sms_opt_out">
                    <radio-group v-model="optOutModel.sms_opt_out_type" inline>
                      <radio
                        v-if="
                          optOutModel.sms_opt_out_types_supported.includes &&
                            optOutModel.sms_opt_out_types_supported.includes(
                              smsOptOutTypeEnum.LINK
                            )
                        "
                        :label="smsOptOutTypeEnum.LINK"
                        :border="true"
                        :title="$t('SmsSettings_OptOutType_Link')"
                      />
                      <radio
                        v-if="
                          optOutModel.sms_opt_out_types_supported.includes &&
                            optOutModel.sms_opt_out_types_supported.includes(
                              smsOptOutTypeEnum.REPLY
                            )
                        "
                        :label="smsOptOutTypeEnum.REPLY"
                        :border="true"
                        :title="$t('SmsSettings_OptOutType_Reply')"
                      />
                    </radio-group>
                  </form-item>
                </el-col>
              </el-row>
              <form-item
                v-if="
                  optOutModel.sms_opt_out &&
                    optOutModel.sms_opt_out_type === smsOptOutTypeEnum.REPLY
                "
              >
                <form-section :title="$t('SmsSettings_OptOutReply_Title')">
                  <template slot="controls">
                    <el-button
                      type="primary"
                      icon="el-icon-circle-plus-outline"
                      @click="createSmsOptOutReply"
                    >
                      {{ $t('SmsSettings_OptOutReply_CreateButton') }}
                    </el-button>
                  </template>
                  <rest-list-editable
                    :payload="fetchSmsOptOutReplyEndpoint"
                    :rules="smsOptOutReplyRules"
                    :add-endpoint="addSmsOptOutReplyEndpoint"
                    :update-endpoint="updateSmsOptOutReplyEndpoint"
                    :outline="false"
                    :filterable="false"
                    ref="reply-list"
                  >
                    <rest-list-column-enum
                      :editable="true"
                      prop="language"
                      :title="$t('SmsSettings_OptOutReply_Language')"
                      :enum="languages"
                      :translate="false"
                      :select-search="true"
                    />
                    <rest-list-column
                      :editable="true"
                      prop="message"
                      :title="$t('SmsSettings_OptOutReply_Message')"
                    />
                    <rest-list-column
                      :editable="true"
                      prop="keyword"
                      :title="$t('SmsSettings_OptOutReply_Keyword')"
                    >
                      <template slot="input-edit" slot-scope="{ model }">
                        <el-input
                          v-model="model.keyword"
                          :disabled="!!model.inUse"
                        />
                      </template>
                    </rest-list-column>
                    <rest-list-column-actions :editable="true">
                      <template slot-scope="scope">
                        <rest-list-action-delete
                          :endpoint="rowDeleteSmsOptOutReplyEndpoint(scope.row)"
                          :name="scope.row.keyword"
                          v-if="!scope.row.inUse"
                        />
                      </template>
                    </rest-list-column-actions>
                  </rest-list-editable>
                </form-section>
              </form-item>
            </el-form>
          </form-section>
        </rest-form>
      </rest-page>
      <rest-page
        v-if="isSmsAdminSettingsVisible"
        :fetch-endpoint="fetchExtendedSmsShortenUrlEndpoint"
      >
        <rest-form
          ref="shorten-url-form"
          :save-endpoint="saveProjectEndpoint"
          :model="smsShortenUrlModel"
          :disabled="isDisabled"
        >
          <form-section>
            <el-form :model="smsShortenUrlModel" :disabled="isDisabled">
              <form-section-header
                :title="$t('SmsSettings_ShortenUrlSettings_Title')"
              >
                <template slot="controls">
                  <save-button
                    :click="() => $refs['shorten-url-form'].save()"
                  />
                </template>
              </form-section-header>
              <el-row>
                <el-col :span="12">
                  <form-item class="sms-opt-out">
                    <toggle
                      v-model="smsShortenUrlModel.sms_shorten_url"
                      :text="$t('SmsSettings_ShortenUrl_Title')"
                      :disabled="isSmsAdminSettingsDisabled"
                    />
                  </form-item>
                </el-col>
              </el-row>
              <div v-if="smsShortenUrlModel.sms_shorten_url">
                <el-row>
                  <el-col :span="12">
                    <form-item class="sms-opt-out">
                      <toggle
                        v-model="
                          smsShortenUrlModel.sms_shorten_url_domain_enabled
                        "
                        :disabled="isSmsAdminSettingsDisabled"
                        :text="$t('SmsSettings_ShortenUrl_DomainEnabled')"
                      />
                    </form-item>
                  </el-col>
                </el-row>
                <el-row
                  v-if="smsShortenUrlModel.sms_shorten_url_domain_enabled"
                >
                  <el-col :span="12">
                    <form-item>
                      <el-input
                        v-model="smsShortenUrlModel.sms_shorten_url_domain"
                        :disabled="isSmsAdminSettingsDisabled"
                      />
                    </form-item>
                  </el-col>
                </el-row>
              </div>
            </el-form>
          </form-section>
        </rest-form>
      </rest-page>
      <rest-page
        v-if="isSmsAdminSettingsVisible"
        :fetch-endpoint="fetchAdvancedBotFilterEndpoint"
      >
        <rest-form
          ref="advanced-bot-filter-form"
          :save-endpoint="saveProjectEndpoint"
          :model="advancedBotFilterModel"
          :disabled="isDisabled"
        >
          <form-section>
            <el-form :model="advancedBotFilterModel" :disabled="isDisabled">
              <form-section-header
                :title="$t('SmsSettings_Advanced_Bot_Detection_Title')"
              >
                <template slot="controls">
                  <save-button
                    :click="() => $refs['advanced-bot-filter-form'].save()"
                  />
                </template>
              </form-section-header>
              <el-row>
                <el-col :span="12">
                  <form-item class="advanced-bot-filter">
                    <toggle
                      v-model="advancedBotFilterModel.advanced_sms_bot_filter"
                      :text="
                        $t('SmsSettings_Advanced_Bot_Detection_Description')
                      "
                      :disabled="isSmsAdminSettingsDisabled"
                    />
                  </form-item>
                </el-col>
              </el-row>
            </el-form>
          </form-section>
        </rest-form>
      </rest-page>
    </page-content>
  </page>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import FormSection from '@/common/components/form/FormSection'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListEditable from '@/common/components/rest/RestListEditable'
import IconUpload from '@/common/components/form/IconUpload'
import ColorPickerField from '@/common/components/form/ColorPickerField'
import Toggle from '@/common/components/form/Toggle'
import UnsubscribePreview from '@/modules/settings/components/UnsubscribePreview'
import FormItem from '@/common/components/form/FormItem'
import SaveButton from '@/common/components/form/SaveButton'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import FormSectionHeader from '@/common/components/form/FormSectionHeader'
import Radio from '@/common/components/form/Radio'
import RadioGroup from '@/common/components/form/RadioGroup'
import { smsOptOutTypeEnum } from '@/modules/settings/enum/SmsOptOutTypeEnum'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import Page from '@/common/components/page/Page'
import PageContent from '@/common/components/page/PageContent'

export default {
  name: 'SmsSettingsPage',
  components: {
    PageContent,
    Page,
    PageHeaderNew,
    RestListColumnEnum,
    FormSectionHeader,
    RestForm,
    RestPage,
    SaveButton,
    FormItem,
    UnsubscribePreview,
    Toggle,
    ColorPickerField,
    IconUpload,
    RestListEditable,
    RestListActionDelete,
    FormSection,
    RestListColumnActions,
    RestListColumn,
    RadioGroup,
    Radio
  },
  data() {
    return {
      subscriptionModel: {
        sms_subscription_page: {
          logo: '',
          background: '',
          mobile_number_change_enabled: 0
        }
      },
      optOutModel: {
        sms_opt_out: null,
        sms_opt_out_type: null,
        sms_opt_out_types_supported: null
      },
      advancedBotFilterModel: {
        advanced_sms_bot_filter: null
      },
      smsShortenUrlModel: {
        sms_shorten_url: null,
        sms_shorten_url_domain: null,
        sms_shorten_url_domain_enabled: 0
      },
      languages: {},
      smsOptOutTypeEnum
    }
  },
  methods: {
    updateSmsFromNameEndpoint(row) {
      return projectEndpoint({
        method: 'patch',
        url: `sms-from-names/${row.id}`
      })
    },
    rowDeleteSmsFromNameEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `sms-from-names/${row.id}`
      })
    },
    createSmsFromName() {
      this.$refs['from-name-list'].addRow({
        from_name: ''
      })
    },
    updateSmsOptOutReplyEndpoint(row) {
      return projectEndpoint({
        method: 'patch',
        url: `sms-reply-to-messages/${row.id}`
      })
    },
    rowDeleteSmsOptOutReplyEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `sms-reply-to-messages/${row.id}`
      })
    },
    createSmsOptOutReply() {
      this.$refs['reply-list'].addRow({
        inUse: false
      })
    }
  },
  watch: {
    optOutModel: {
      handler(newValue) {
        if (
          newValue.sms_opt_out &&
          newValue.sms_opt_out_type &&
          newValue.sms_opt_out_types_supported.includes &&
          !newValue.sms_opt_out_types_supported.includes(
            newValue.sms_opt_out_type
          )
        ) {
          this.optOutModel.sms_opt_out_type =
            this.optOutModel.sms_opt_out_types_supported.length >= 1
              ? this.optOutModel.sms_opt_out_types_supported[0]
              : null
        }
      },
      deep: true
    },
    smsShortenUrlModel: {
      handler(newValue) {
        if (newValue.sms_shorten_url_domain_enabled === 0) {
          this.smsShortenUrlModel.sms_shorten_url_domain = null
        }
      },
      deep: true
    }
  },
  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('Settings_Breadcrumb_Settings')
        },
        {
          label: this.$t('Settings_Breadcrumb_Channels'),
          route: projectRoute({
            name: 'EnableChannelsTab'
          })
        },
        {
          label: this.$t('Channels_SmsSettings')
        }
      ]
    },
    saveProjectEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: ''
      })
    },
    fetchSmsSubscriptionEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: { expand: 'sms_subscription_page' },
        handler: data => {
          this.subscriptionModel = {
            sms_subscription_page: {
              ...data.sms_subscription_page
            }
          }
        }
      })
    },
    fetchAdvancedBotFilterEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: { expand: 'advanced_sms_bot_filter' },
        handler: data => {
          this.advancedBotFilterModel = {
            advanced_sms_bot_filter: data.advanced_sms_bot_filter
          }
        }
      })
    },
    fetchExtendedSmsOptOutEndpoint() {
      return [this.fetchSmsOptOutEndpoint, this.fetchLanguagesEndpoint]
    },
    fetchExtendedSmsShortenUrlEndpoint() {
      return [this.fetchSmsShortenUrlEndpoint, this.fetchLanguagesEndpoint]
    },
    fetchSmsShortenUrlEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: {
          expand: 'sms_shorten_url,sms_shorten_url_domain'
        },
        handler: data => {
          this.smsShortenUrlModel.sms_shorten_url = data.sms_shorten_url
          this.smsShortenUrlModel.sms_shorten_url_domain =
            data.sms_shorten_url_domain
          this.smsShortenUrlModel.sms_shorten_url_domain_enabled = data.sms_shorten_url_domain
            ? 1
            : 0
        }
      })
    },
    fetchSmsOptOutEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: ``,
        params: {
          expand: 'sms_opt_out,sms_opt_out_type,sms_opt_out_types_supported'
        },
        handler: data => {
          this.optOutModel = {
            sms_opt_out: data.sms_opt_out,
            sms_opt_out_type: data.sms_opt_out_type,
            sms_opt_out_types_supported: data.sms_opt_out_types_supported
          }
        }
      })
    },
    fetchLanguagesEndpoint() {
      return {
        method: 'get',
        url: `languages`,
        handler: data => {
          this.languages = data
        }
      }
    },
    fetchSmsFromNameEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `sms-from-names`,
        params: {}
      })
    },
    fetchSmsOptOutReplyEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `sms-reply-to-messages`,
        params: {}
      })
    },
    addSmsFromNameEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `sms-from-names`
      })
    },
    addSmsOptOutReplyEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `sms-reply-to-messages`
      })
    },
    logo() {
      return (
        this.subscriptionModel.sms_subscription_logo_file ||
        this.subscriptionModel.sms_subscription_page.logo
      )
    },

    isSubscriptionPreferencesEnabled() {
      return this.$store.state.app.features.subscriptionPreferences
    },
    isDisabled() {
      return !this.$can('updateProject')
    },
    smsFromNameRules() {
      return {
        from_name: [
          {
            required: true
          },
          {
            max: 11,
            message: this.$t(
              'ProjectSettings_ChannelSettings_Sms_Sender_Validation_Min_Max'
            )
          },
          {
            pattern: /^[a-z0-9 ]+$/i,
            message: this.$t(
              'ProjectSettings_ChannelSettings_Sms_Sender_Validation_Alphanumeric'
            )
          }
        ]
      }
    },
    smsOptOutReplyRules() {
      return {
        keyword: [
          {
            required: true,
            message: this.$t('SmsSettings_OptOutReply_KeywordIsRequired')
          }
        ],
        message: [
          {
            required: true,
            message: this.$t('SmsSettings_OptOutReply_MessageIsRequired')
          }
        ]
      }
    },
    isSmsAdminSettingsVisible() {
      return this.$can('XpSmsConfiguration:ReadAccess')
    },
    isSmsAdminSettingsDisabled() {
      return !this.$can('XpSmsConfiguration:FullAccess')
    }
  }
}
</script>
<style scoped>
.page-styles {
  display: flex;
}

.page-styles > * {
  margin-right: 10px;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/pages/SmsSettingsPage.vue